import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';

// 导入真实 API 和模拟 API
import * as realApi from '../../api/noMatchApi';
import * as mockApi from '../../api/mockNoMatchApi';

// 控制是否使用模拟数据
const USE_MOCK_API = false; // 設置為 false 與 index.js 保持一致，使用真實 API

// 选择要使用的 API
const api = USE_MOCK_API ? mockApi : realApi;

const Settings = () => {
  const navigate = useNavigate();
  
  // 状态管理
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // 表单状态
  const [formData, setFormData] = useState({
    name: '',
    userGender: 'prefer_not_to_say', // 用戶自己的性別
    preferences: {
      preferredGender: 'any', // 配對偏好性別
      gender: 'any', // 保持向後相容性
    }
  });

  // 组件加载时获取用户信息
  useEffect(() => {
    loadUserProfile();
  }, []);

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ESC 键返回上一页
      if (e.key === 'Escape') {
        goBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 加载用户资料
  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      setError(null); // 清除之前的錯誤
      
      console.log('🔄 Loading user profile...');
      const userProfile = await api.getUserProfile();
      console.log('📊 User profile loaded:', userProfile);
      
      if (!userProfile) {
        console.log('❌ No user profile found, redirecting to main page');
        navigate('/nomatch');
        return;
      }
      
      setUser(userProfile);
      
      // 安全地設置 formData，提供所有必要的預設值
      const safeFormData = {
        name: userProfile.name || '',
        userGender: userProfile.userGender || 'prefer_not_to_say',
        preferences: {
          preferredGender: userProfile.preferences?.preferredGender || userProfile.preferences?.gender || 'any',
          gender: userProfile.preferences?.gender || 'any',
        }
      };
      
      console.log('📝 Setting form data:', safeFormData);
      setFormData(safeFormData);
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      setError(`無法載入用戶資料: ${error.message || '未知錯誤'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 驗證表單資料
    if (!formData.name || !formData.name.trim()) {
      setError('請輸入您的名字');
      return;
    }

    if (!formData.userGender) {
      setError('請選擇您的性別');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage('');
      
      const userData = {
        name: formData.name.trim(),
        userGender: formData.userGender,
        preferences: {
          preferredGender: formData.preferences.preferredGender || 'any',
          gender: formData.preferences.gender || formData.preferences.preferredGender || 'any', // 確保向後相容性
        },
      };
      
      console.log('💾 Submitting user data:', userData);
      const response = await api.saveUserProfile(userData);
      console.log('✅ Save response:', response);
      
      if (response && response.success) {
        setUser(response.user);
        setSuccessMessage('設置已成功保存');
        
        // 3秒后清除成功消息
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setError('保存失敗：伺服器回應異常');
      }
    } catch (error) {
      console.error('❌ Error saving user profile:', error);
      setError(`保存設置時發生錯誤：${error.message || '請重試'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 处理表单字段变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'name') {
      setFormData(prev => ({
        ...prev,
        name: value
      }));
    } else if (name === 'userGender') {
      setFormData(prev => ({
        ...prev,
        userGender: value
      }));
    } else if (name === 'preferredGender') {
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          preferredGender: value,
          gender: value // 保持向後相容性
        }
      }));
    } else if (name === 'gender') {
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          gender: value
        }
      }));
    }
  };

  // 返回上一页
  const goBack = () => {
    // 检查是否有历史记录可以回退
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // 如果没有历史记录，则跳转到主页
      navigate('/nomatch');
    }
  };

  // 清除所有数据（重置）
  const handleReset = () => {
    if (window.confirm('確定要重置所有數據嗎？這將清除您的所有聊天記錄和設置。')) {
      try {
        // 清除 localStorage 中的数据
        localStorage.removeItem('noMatchUserProfile');
        
        // 重定向到主页，将触发重新设置流程
        navigate('/nomatch');
        
        // 刷新页面以确保完全重置
        window.location.reload();
      } catch (error) {
        console.error('Error resetting data:', error);
        setError('重置數據時發生錯誤');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="settings-loading">
        <div className="loading-spinner"></div>
        <p>載入設置中...</p>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-content">
        {/* AI 风格的头部 */}
        <header className="ai-header">
          <button onClick={goBack} className="menu-btn" title="返回上一頁">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h2 className="ai-title">NoMatch 設置 {USE_MOCK_API ? '(演示模式)' : ''}</h2>
          <div className="header-placeholder">
            {/* 右侧占位，保持布局平衡 */}
          </div>
        </header>

        {/* 成功/错误消息 */}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* 设置表单 */}
        <form onSubmit={handleSubmit} className="settings-form">
          {/* 基本信息 */}
          <section className="settings-section">
            <h2>基本信息</h2>
            
            <div className="form-group">
              <label htmlFor="name">顯示名稱</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="請輸入您的名字"
                required
              />
              <small className="form-help">
                這是其他用戶看到的您的名字
              </small>
            </div>
          </section>

          {/* 匹配偏好 */}
          <section className="settings-section">
            <h2>匹配偏好</h2>
            
            <div className="form-group">
              <label htmlFor="userGender">您的性別</label>
              <select
                id="userGender"
                name="userGender"
                value={formData.userGender}
                onChange={handleInputChange}
              >
                <option value="prefer_not_to_say">不願意說明</option>
                <option value="male">男性</option>
                <option value="female">女性</option>
              </select>
              <small className="form-help">
                這是您的性別，系統會根據您的偏好進行配對
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="preferredGender">希望配對的性別</label>
              <select
                id="preferredGender"
                name="preferredGender"
                value={formData.preferences.preferredGender}
                onChange={handleInputChange}
              >
                <option value="any">任何人</option>
                <option value="male">男性</option>
                <option value="female">女性</option>
              </select>
              <small className="form-help">
                系統會根據您的偏好進行配對
              </small>
            </div>
          </section>

          {/* 操作按钮 */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="save-btn"
              disabled={isSaving}
            >
              {isSaving ? '保存中...' : '保存設置'}
            </button>
          </div>
        </form>

        {/* 危险操作区域 */}
        <section className="settings-section danger-zone">
          <h2>危險操作</h2>
          <p>以下操作不可逆，請謹慎操作</p>
          
          <button 
            onClick={handleReset}
            className="reset-btn"
          >
            重置所有數據
          </button>
          <small className="form-help">
            這將清除您的所有聊天記錄、設置和帳戶信息
          </small>
        </section>

        {/* 应用信息 */}
        <section className="settings-section app-info">
          <h2>關於 NoMatch</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>版本</strong>
              <span>1.0.0</span>
            </div>
            <div className="info-item">
              <strong>模式</strong>
              <span>{USE_MOCK_API ? '演示模式' : '在線模式'}</span>
            </div>
            <div className="info-item">
              <strong>聊天室過期時間</strong>
              <span>24小時</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings; 